(ns nosco.shared.submission-form
  (:require
   #? (:clj [nosco.i18n.translate-server :as t]
       :cljs [nosco.i18n.translate :as t])
   [clojure.string :as string]
   [medley.core :as medley]
   [nosco.shared.form-fields :as form-fields]
   [nosco.util.diff :as diff]))

(defn find-by-id
  "Find the first item in coll whose :id equals id. Returns nil when not found."
  [coll id]
  (first (filter (comp #{id} :id) coll)))

(defn field-options
  "The configured options of a select field as [{:id :title :color}], or nil
   when the field has none. `form` is a submission form (a seq of field maps,
   e.g. a channel's :masterSubmissionForm). The single reader for a field's
   available option menu — used by :control/:filter option sources."
  [form field-id]
  (some->> (:options (find-by-id form field-id))
           (mapv #(select-keys % [:id :title :color]))
           not-empty))

(defn section-definition-error
  "Validate complete master-form section relationships, never a permission-filtered
   projection. Accepts stored or GraphQL field types; returns an error or nil."
  [form]
  (when (some #(or (form-fields/section-type? (:type %))
                   (some? (get-in % [:settings :sectionId]))) form)
    (let [by-id (into {} (map (juxt :id identity)) form)]
      (or
       (when (not= (count form) (count by-id)) "field ids must be unique")
       (when-not (form-fields/title-type? (:type (first form))) "TITLE must be first")
       (some (fn [{:keys [id type title required settings stageOverrides]}]
               (let [section-id (:sectionId settings)]
                 (cond
                   (and section-id (or (form-fields/title-type? type)
                                       (form-fields/section-type? type)))
                   (str "TITLE and SECTION cannot belong to a section: " id)
                   (and section-id (not (form-fields/section-type? (:type (get by-id section-id)))))
                   (str "sectionId must reference a SECTION header: " id)
                   ;; header visibility is derived from its members, see `remove-hidden-sections`
                   (and (form-fields/section-type? type) (string/blank? title))
                   (str "SECTION must have a title: " id)
                   (and (form-fields/section-type? type) (some (comp some? val) settings))
                   (str "SECTION headers do not support settings: " id)
                   (and (form-fields/section-type? type) (not (false? required)))
                   (str "SECTION must have required=false: " id)
                   (and (form-fields/section-type? type)
                        (some #(or (some? (:required %)) (some? (:readonly %))) stageOverrides))
                   (str "SECTION stage overrides only support visibility: " id))))
             form)
       (loop [[{:keys [id type settings] :as field} & remaining] form
              current-section nil]
         (when field
           (let [section-id (:sectionId settings)]
             (cond
               (form-fields/section-type? type) (recur remaining id)
               (nil? section-id) (recur remaining nil)
               (= section-id current-section) (recur remaining current-section)
               :else (str "section members must follow their header contiguously: " id)))))))))

(defn remove-hidden-sections
  "Removes the SECTION headers whose members were all filtered out of `visible-fields`,
   a permission projection of `fields`, so their titles don't leak. Headers without any
   members in `fields` stay: empty sections are valid definitions that the form editor
   must round-trip. Pass `:field-fn` when items wrap their field definition, e.g.
   `:ideaboxTemplateItem` for idea templates."
  [fields visible-fields & {:keys [field-fn] :or {field-fn identity}}]
  (let [section-ids (fn [items] (into #{} (keep #(get-in (field-fn %) [:settings :sectionId])) items))
        sections-with-members (section-ids fields)
        sections-with-visible-members (section-ids visible-fields)]
    (remove (fn [item]
              (let [{:keys [id type]} (field-fn item)]
                (and (form-fields/section-type? type)
                     (contains? sections-with-members id)
                     (not (contains? sections-with-visible-members id)))))
            visible-fields)))

(defn group-sections
  "The fields a user sees, grouped for display: `{:field f}` for ungrouped fields
   and `{:section header :fields [f ...]}` at the header's place, members in
   `fields` order. `fields` are the ones to show, already filtered by stage
   visibility, conditions and so on, so a header that was filtered out leaves its
   members ungrouped, and a header with no members left isn't shown. Pass
   `:field-fn` when items wrap their field definition."
  [fields & {:keys [field-fn] :or {field-fn identity}}]
  (let [header-ids (into #{} (comp (map field-fn)
                                   (filter #(form-fields/section-type? (:type %)))
                                   (map :id))
                         fields)
        section-id (fn [field]
                     (let [id (get-in (field-fn field) [:settings :sectionId])]
                       (when (contains? header-ids id) id)))
        members (group-by section-id fields)]
    (into []
          (keep (fn [field]
                  (cond
                    (form-fields/section-type? (:type (field-fn field)))
                    (when-let [fields (get members (:id (field-fn field)))]
                      {:section field :fields fields})

                    (nil? (section-id field))
                    {:field field})))
          fields)))

(def default-field-visibility #{:ADMINS :AUTHORS :EVALUATORS :MEMBERS})

(def default-field-editability #{:ADMINS :AUTHORS})

(def field-visibilities
  "The visibility sets the form editor can produce (see the field permissions
   dialog): admins always; members only with authors and evaluators."
  #{default-field-visibility
    #{:ADMINS :AUTHORS :EVALUATORS}
    #{:ADMINS :EVALUATORS}
    #{:ADMINS :AUTHORS}
    #{:ADMINS}})

(def field-editabilities
  "The editability sets the form editor can produce: admins always, authors
   optionally."
  #{default-field-editability
    #{:ADMINS}})

(defn field-visibility?
  "Whether `whos` (keywords or strings) is a visibility the form editor can
   produce; other sets break the idea editor."
  [whos]
  (contains? field-visibilities (into #{} (map keyword) whos)))

(defn field-editability?
  "Whether `whos` (keywords or strings) is an editability the form editor can
   produce."
  [whos]
  (contains? field-editabilities (into #{} (map keyword) whos)))

(defn settings->editability [{:keys [editability]}]
  (if (empty? editability)
    default-field-editability
    (into #{} (map keyword) editability)))

(defn settings->visibility [{:keys [visibility]}]
  (if (empty? visibility)
    default-field-visibility
    (into #{} (map keyword) visibility)))

(defn viewer-channel-admin?
  "Is `viewer` an admin of the channel `ideabox-id` - either globally, or for that
   channel alone?

   `viewer` is the app-level user: :isAdmin covers only global admins, and the
   per-channel grants live in :administeredIdeaboxes. This is the portable twin of
   `nosco.controllers/viewer-channel-admin?` (cljs-only, reads the app store) and of
   the server's `(or (db-base/admin? viewer) (db-base/ideabox-admin? viewer id))`.
   Checking :isAdmin alone silently demotes channel admins to regular members."
  [viewer ideabox-id]
  (or (boolean (:isAdmin viewer))
      (contains? (set (:administeredIdeaboxes viewer)) ideabox-id)))

(defn idea-viewer-roles
  "The viewer's roles relative to `idea`, shaped as the argument map expected by
   `idea-template-field-visible?` / `idea-template-field-can-edit?` (minus :idea
   and :field)."
  [idea viewer]
  {:viewer-admin? (viewer-channel-admin? viewer (-> idea :ideabox :id))
   :viewer-author? (= (:id viewer) (-> idea :author :id))
   :viewer-owner? (boolean (some #(= (:id %) (:id viewer)) (:owners idea)))
   :viewer-editor? (boolean (some #(= (:id %) (:id viewer)) (:editors idea)))
   :viewer-evaluator? (boolean (:isViewerEvaluator idea))})

(defn idea-template-field-visible? [{:keys [viewer-admin? viewer-evaluator? viewer-author? viewer-owner? viewer-editor? idea field]}]
  (let [visibility (settings->visibility (:settings field))
        idea-state (-> idea :state keyword)
        idea-published? (or (= idea-state :PUBLISHED)
                            (= idea-state :publish))]
    (cond (and (contains? visibility :ADMINS) viewer-admin?)
          true
          (and (contains? visibility :AUTHORS)
               (or (not idea-published?)
                   viewer-author?
                   ;; Disabled for now, to be used when we move from author to owners
                   #_viewer-owner?
                   viewer-editor?))
          true
          (and (contains? visibility :EVALUATORS)
               idea-published?
               viewer-evaluator?)
          true
          (contains? visibility :MEMBERS)
          true
          :else
          false)))

(defn stage-template-field-visible? [{:keys [viewer-admin? field]}]
  (let [visibility (settings->visibility (:settings field))]
    (cond (and (contains? visibility :ADMINS) viewer-admin?)
          true
          (contains? visibility :AUTHORS)
          true
          (contains? visibility :EVALUATORS)
          true
          (contains? visibility :MEMBERS)
          true
          :else
          false)))

(defn idea-template-field-can-edit? [{:keys [viewer-admin? viewer-author? viewer-owner? viewer-editor? idea field]}]
  (let [editability (settings->editability (:settings field))
        idea-state (-> idea :state keyword)
        idea-published? (= idea-state :PUBLISHED)]
    (cond (and (contains? editability :ADMINS)
               viewer-admin?)
          true
          (and (contains? editability :AUTHORS)
               (or (not idea-published?)
                   viewer-author?
                   ;; Disabled for now, to be used when we move from author to owners
                   #_viewer-owner?
                   viewer-editor?))
          true
          :else
          false)))

(defn stage-template-field-can-edit? [{:keys [viewer-admin? field]}]
  (let [editability (settings->editability (:settings field))]
    (cond (and (contains? editability :ADMINS) viewer-admin?)
          true
          (contains? editability :AUTHORS)
          true
          (contains? editability :MEMBERS)
          true
          :else
          false)))

;;; Stage overrides

(def default-stage-overrides
  {:visible true
   :readonly false
   ;; required nil means, use the field-level required value
   ;; this will eventually get resolved to true/false
   :required nil})

(defn lookup-stage-override
  "Given the stageOverrides of a field, return the overrides for the given stage-id, falling back
   to the default stage overrides if the stage is not found"
  [field stage-id]
  (let [stage-overrides (:stageOverrides field)]
    (merge
     (assoc default-stage-overrides :stageId (str stage-id))
     (first
      (filter (fn [{:keys [stageId]}]
                (= (str stageId) (str stage-id)))
              stage-overrides)))))

;;; Utilities to process submission forms
(defn add-stage-overrides
  "Add ::stage-visibility, ::stage-readonly, and override :required to the fields in the master form,
  based on the stage overrides"
  [master-form stage-id]
  (->> master-form
       (map (fn [field]
              (let [current (lookup-stage-override field stage-id)
                    required-override (:required current)
                    required-field (:required field)
                    required-applied (if (some? required-override)
                                       required-override
                                       required-field)]
                (assoc field
                       ::stage-readonly (:readonly current)
                       ::stage-visibility (:visible current)
                       :required-master-field required-field ;; only for debugging
                       :required required-applied))))))

(defn stage-overrides-need-update?
  "Are the stage overrides changed between stages, such as the ideas
   need to be updated?"
  [master-form current-stage-id new-stage-id]
  (let [master-form-current (add-stage-overrides master-form current-stage-id)
        master-form-new (add-stage-overrides master-form new-stage-id)
        changes (map (fn [current-field new-field]
                       (let [current-overrides (:stageOverrides current-field)
                             new-overrides (:stageOverrides new-field)]
                         (or
                          ;; something wasn't required, and now is
                          (and (not (:required current-field))
                               (:required new-field))
                          ;; something wasn't visible, and now is
                          (and (not (::stage-visibility current-overrides))
                               (::stage-visibility new-overrides)))))
                     master-form-current
                     master-form-new)]
    (some true? changes)))

;; Make required-and-not-editable fields not-required
(defn- update-stage-template-items-required [template ideabox viewer]
  (let [viewer-admin? (viewer-channel-admin? viewer (:id ideabox))]
    (->> (for [field template]
           (let [can-edit? (stage-template-field-can-edit? {:viewer-admin? viewer-admin?
                                                            :field field})]
             (if can-edit?
               field
               (assoc field :required false))))
         vec)))

(defn stage-visible-field-ids [form stage-id]
  (let [with-visibility (add-stage-overrides form stage-id)]
    (->> with-visibility
         (filter ::stage-visibility)
         (map :id)
         (into #{}))))

(defn idea-view-fields
  "The idea's answers (`template`, its `{:ideaboxTemplateItem :value}` items) to
   show on the idea view, in the current master form's order and with its
   section headers, ready for `group-sections` with `:field-fn ::form-field`.
   Each item gets `::form-field`, the definition to group by: the master form's,
   or the stored one for a legacy form or a field removed from the form.

   Shows the stage's visible fields that have a value, and their headers. With
   `show-hidden-fields`, also the hidden fields and the removed ones (marked
   with `::stage-visibility` false and `::form-change-type ::removal`), and
   all the headers, removed ones included.

   Without a master form (a channel on per-stage forms), preserves the stored
   answers' order and definitions in the same normalized format."
  [template master-form stage-id show-hidden-fields]
  (let [answered? (fn [field value]
                    (and (not (form-fields/title-type? (:type field)))
                         (form-fields/some-value? value)))
        stage-visible-ids (stage-visible-field-ids master-form stage-id)
        master-by-id (into {} (map (juxt :id identity)) master-form)
        items-by-id (into {} (map (juxt #(get-in % [:ideaboxTemplateItem :id]) identity)) template)
        layout (diff/layout-ids (map #(get-in % [:ideaboxTemplateItem :id]) template)
                                (map :id master-form))]
    (if (nil? master-form)
      (into []
            (comp (filter #(answered? (:ideaboxTemplateItem %) (:value %)))
                  (map #(assoc % ::form-field (:ideaboxTemplateItem %))))
            template)
      (into []
            (keep (fn [{:keys [id]}]
                    (let [master-field (get master-by-id id)
                          item (get items-by-id id)
                          field (or master-field (:ideaboxTemplateItem item))
                          removed (nil? master-field)
                          stage-visible (contains? stage-visible-ids id)]
                      (cond
                        (form-fields/section-type? (:type field))
                        (when (or stage-visible show-hidden-fields)
                          (cond-> (assoc (or item {:ideaboxTemplateItem field}) ::form-field field)
                            removed (assoc ::form-change-type ::removal)))

                        (and (answered? field (:value item))
                             (or stage-visible show-hidden-fields))
                        (cond-> (assoc item ::form-field field)
                          show-hidden-fields (assoc ::stage-visibility stage-visible)
                          removed (assoc ::form-change-type ::removal))))))
            layout))))

(defn- keywordize-type [template-item]
  (update template-item :type keyword))

(defn- conditional-options-set [template-item]
  (medley/update-existing-in template-item [:settings :conditional :optionIds] set))

(defn process-template-item
  "Convert a GraphQL templateItem to be used in a form state."
  [template-item]
  (->
   template-item
   (keywordize-type)
   (conditional-options-set)))

(defn ideabox->stage-submission-form
  "Returns a submission form ready to be used to create a new idea or update an idea to a new stage
    * either uses masterSubmissionForm if exists, or the stage ideaTemplate
    * adds a :stage-visibility boolean to the masterSubmission form
    * ensures that non editable fields are not required
    * ensures that graphql types are keywordized and made into sets as needed

     "
  [ideabox stage viewer]
  (let [master-form (some-> (:masterSubmissionForm ideabox)
                            (add-stage-overrides (:id stage)))
        default-stage-template (:ideaTemplate stage)
        form-to-use (map process-template-item
                         (or master-form default-stage-template))]

    (update-stage-template-items-required form-to-use ideabox viewer)))

(defn ideabox->initial-submission-form
  "Returns a submission form ready to be used to create a new idea
    * either uses masterSubmissionForm if exists, or the defaultStage ideaTemplate
    * adds a ::stage-visibility boolean to the masterSubmission form
    * ensures that non editable fields are not required
    * ensures that graphql types are keywordized and made into sets as needed

     "
  [ideabox viewer]
  (ideabox->stage-submission-form ideabox (:defaultStage ideabox) viewer))

(defn compute-unified-options [idea-item stage-item]
  ;; we use the diff algorithm to get as close to the correct order as possible
  (let [idea-options (get idea-item :options)
        idea-options-map (into {} (map (juxt :id identity)) idea-options)
        stage-options (get stage-item :options)
        stage-options-map (into {} (map (juxt :id identity)) stage-options)
        idea-option-ids (map :id idea-options)
        stage-options-ids (map :id stage-options)
        diff (diff/layout-ids idea-option-ids stage-options-ids)
        unified-ids (mapv :id diff)]
    (for [id unified-ids]
      {:idea-option (get idea-options-map id)
       :stage-option (get stage-options-map id)})))

(defn update-idea-template-items-required [idea-template idea viewer]
  (let [viewer-roles (idea-viewer-roles idea viewer)]
    (mapv (fn [field]
            (let [can-edit? (idea-template-field-can-edit?
                             (assoc viewer-roles :idea idea :field field))]
              (cond-> field
                (not can-edit?)
                (assoc :required false)))) idea-template)))

(defn- compute-selected-options [options]
  (into #{}
        (comp (filter :selected) (map :id))
        options))

;; we want to always show all the options already in the idea template
;; and if there's new elements in the stage, show them too
(defn- merge-field-options [unified-options selected-options]
  (->> (for [{:keys [idea-option stage-option]} unified-options
             ;; Remove options that have been removed from the stage template and are unselected
             :when (or stage-option (contains? selected-options (:id idea-option)))]
         ;; Return the stage first since it might have
         ;; edits in the titles etc - then fallback to the
         ;; idea for removed options for completeness
         (or stage-option idea-option))
       (mapv #(select-keys % [:id :title :defaultSelected :color]))))

(defn idea->submission-form-legacy
  "Return the submission form for an idea, (without values!), somewhat updated based on the stage template,
  the stage submission form and editability/required fields "
  [idea]
  (let [idea-template (:template idea)
        idea-stage (:stage idea)
        stage-template (map process-template-item (:ideaTemplate idea-stage))
        stage-template-map (into {} (map (juxt :id identity) stage-template))]
    (for [idea-item idea-template
          :let [ideabox-template-item (-> idea-item
                                          :ideaboxTemplateItem
                                          process-template-item)
                stage-item (get stage-template-map (:id ideabox-template-item))

                type (:type ideabox-template-item)
                unified-options (compute-unified-options (:ideaboxTemplateItem idea-item) stage-item)
                is-select-type? (form-fields/option-field-type? type)
                merged-options (when is-select-type?
                                 (merge-field-options unified-options
                                                      (-> idea-item :value :options compute-selected-options)))
                merged-order-by (or (-> stage-item :settings :orderBy)
                                    (-> ideabox-template-item :settings :orderBy))]]
      (cond-> ideabox-template-item
        is-select-type?
        (assoc :options merged-options)
        is-select-type?
        (assoc-in [:settings :orderBy] merged-order-by)))))

;; Detecting submission form changes
;; This is only relevant for channels that have a masterSubmissionForm, i.e. they
;; opt-in to this behaviour.
;; There are many scenarios where the submission form of an idea changes
;; - the admin edits the ideabox submission form behind the scenes
;; - the idea moves to a new stage within the same channel
;; - the idea moves to a new stage in a new channel
;; The aim is to help the author go to the new submission form, so instead of
;; showing the side-by-side diff view, we want a single column view, with the
;; changes highlighed per-field:
;;
;; a) the order of the fields is the one of the new form
;; b) new fields are highlighted in green
;; c) same fields are just plain fields
;; d) removed fields are read-only and highlighted in red
;; e) mutated fields are TBD (need to show both titles/descriptions)
;; f) minor changes such as option changes or required etc are silently updated
;;
;; In most cases, the single-column layout would work best as fields are usually added
;; so the focus would be on the few added fields per stage.
;; However, if you move an idea to a completely new channel, the forms might be wildly different
;; so in that case, we can show a side-by-side view just to ease the migration.
;; TODO we can also let the author update the existing form without migrating to the
;; new one.
;; TODO we can always offer the option to show a side-by-side comparison

(defn- idea->submission-form-new
  "Return the submission form for an idea, (without values!), updated to reflect
   changes in the master submission form, e.g. mutations of the master form,
   and stage-based visibility"
  [idea viewer]
  (let [previous-stage-id (-> idea :previousStage :id)
        viewer-roles (idea-viewer-roles idea viewer)
        idea-items (mapv (fn [field]
                           (update field :ideaboxTemplateItem process-template-item))
                         (:template idea))
        master-form (-> (:masterSubmissionForm (:ideabox idea))
                        (add-stage-overrides (:id (:stage idea)))
                        (->> (map process-template-item)
                             ;; The master form is resolved on the Ideabox, which has no
                             ;; idea context, so the server can only filter it with the lax
                             ;; `stage-template-field-visible?`. Fields hidden from this
                             ;; viewer *on this idea* are therefore stripped from
                             ;; (:template idea) but still arrive here - and the diff below
                             ;; would then present them as ::addition. Re-filter with the
                             ;; same idea-level rule that produced `idea-items`, so both
                             ;; sides of the diff agree on what this viewer can see.
                             (filter (fn [field]
                                       (idea-template-field-visible?
                                        (assoc viewer-roles :idea idea :field field))))))
        idea-fields-map (into {} (map (juxt #(-> % :ideaboxTemplateItem :id) identity) idea-items))
        ideabox-template-map (into {} (map (juxt :id identity) master-form))
        layout (diff/layout-ids (map #(-> % :ideaboxTemplateItem :id) idea-items)
                                (map :id master-form))]
    (for [{:keys [id type]} layout
          :let [idea-item (get idea-fields-map id)
                idea-template-item (:ideaboxTemplateItem idea-item)
                ideabox-template-item (get ideabox-template-map id)
                ;; TODO this is not enough - what if the previous stage doesn't exist anymore?
                ;; or the idea is in a new channel? Should we bake somehow the stage-visibility
                ;; into the idea template? perhaps a new field in the stageOverrides called
                ;; lastStageVisibility that is calculated upon save?
                last-stage-visibility (:visible (lookup-stage-override ideabox-template-item
                                                                       previous-stage-id))
                ;; if the stage-visibility changed, we don't highlight that,
                ;; only the physical removals
                ;; a removed SECTION header is marked like any removed field: the UI
                ;; shows it only with the removed members that still point at it
                field-removed (= type :removal)
                ;; a new header is structure, not input to fill in
                field-added (and (not (form-fields/section-type? (:type ideabox-template-item)))
                                 (or (= type :addition)
                                     (and (not last-stage-visibility)
                                          (::stage-visibility ideabox-template-item))))
                field-to-use (if field-removed idea-template-item ideabox-template-item)
                is-select-type? (form-fields/option-field-type? (:type field-to-use))
                merged-options (when is-select-type?
                                 (->> (compute-unified-options idea-template-item ideabox-template-item)
                                      (map (fn [{:keys [idea-option stage-option]}]
                                             ;; Return the stage option first, fall back to idea option
                                             (select-keys (or stage-option idea-option) [:id :title :defaultSelected :color])))))
                merged-order-by (or (-> idea-item :settings :orderBy)
                                    (-> idea-template-item :settings :orderBy))]]
      ;; TODO if the idea already has some content in the addition,
      ;; we don't need to mark it as added
      ;; similarly, if the field is empty and removed, we don't need to mark it as removed
      (cond-> field-to-use
        field-removed
        (assoc ::form-change-type ::removal)

        field-added
        (assoc ::form-change-type ::addition)

        is-select-type?
        (assoc :options merged-options)

        is-select-type?
        (assoc-in [:settings :orderBy] merged-order-by)

        ;; TODO detect changes, but we only care for title/description, not the whole array of checking
        ;; that template_diff is doing
        ))))

(defn idea->submission-form [idea viewer]
  (->
   (if (:masterSubmissionForm (:ideabox idea))
     (idea->submission-form-new idea viewer)
     (idea->submission-form-legacy idea))
   (update-idea-template-items-required idea viewer)
   (vec)))

(defn visibility-message-legacy [#?(:clj locale) settings]
  (let [visibility (settings->visibility settings)]
    (when (not= default-field-visibility visibility)
      (case visibility
        #{:ADMINS :AUTHORS :EVALUATORS} (t/t #?(:clj locale) "Only admins, authors and evaluators can view")
        #{:ADMINS :EVALUATORS} (t/t #?(:clj locale) "Only admins and evaluators can view")
        #{:ADMINS :AUTHORS} (t/t #?(:clj locale) "Only admins and authors can view")
        #{:ADMINS} (t/t #?(:clj locale) "Only admins can view")))))

(defn visibility-message [#?(:clj locale) settings]
  (let [visibility (settings->visibility settings)]
    (when (not= default-field-visibility visibility)
      (case visibility
        #{:ADMINS :AUTHORS :EVALUATORS} (t/t #?(:clj locale) "Only admins, authors, editors and evaluators can view")
        #{:ADMINS :EVALUATORS} (t/t #?(:clj locale) "Only admins and evaluators can view")
        #{:ADMINS :AUTHORS} (t/t #?(:clj locale) "Only admins, authors and editors can view")
        #{:ADMINS} (t/t #?(:clj locale) "Only admins can view")))))

(defn editability-message-legacy [#?(:clj locale) settings]
  (let [editability (settings->editability settings)]
    (when (not= default-field-editability editability)
      (case editability
        #{:ADMINS :AUTHORS :EVALUATORS} (t/t #?(:clj locale) "Only admins, authors and evaluators can edit")
        #{:ADMINS :EVALUATORS} (t/t #?(:clj locale) "Only admins and evaluators can edit")
        #{:ADMINS :AUTHORS} (t/t #?(:clj locale) "Only admins and authors can edit")
        #{:ADMINS} (t/t #?(:clj locale) "Only admins can edit")))))

(defn editability-message [#?(:clj locale) settings]
  (let [editability (settings->editability settings)]
    (when (not= default-field-editability editability)
      (case editability
        #{:ADMINS :AUTHORS :EVALUATORS} (t/t #?(:clj locale) "Only admins, authors, editors and evaluators can edit")
        #{:ADMINS :EVALUATORS} (t/t #?(:clj locale) "Only admins and evaluators can edit")
        #{:ADMINS :AUTHORS} (t/t #?(:clj locale) "Only admins, authors and editors can edit")
        #{:ADMINS} (t/t #?(:clj locale) "Only admins can edit")))))
